// apps/admin/src/components/composer/zone-binding-picker.tsx
//
// P5 UI Polish W6 T8 — Content-binding editor for the PropertiesPanel.
//
// When exactly one zone is selected, this picker appears below X/Y/W/H
// and lets the operator declare WHAT the zone should display:
//
//   Vide          — unbound; zone renders gradient placeholder in preview
//   Image         — cmdk autocomplete of tenant image resources
//   Vidéo         — cmdk autocomplete of tenant video resources
//   Page web      — URL text input
//   Flux RSS      — URL text input
//   Composer imbriqué — cmdk autocomplete of tenant composers
//
// Binding persistence (completed in W8 T2 — this comment block previously
// described a W7 stub that no longer exists):
//   `handleSave` PATCHes the single item via `patchProgramItem(composerId,
//   zoneId, binding)` → server `PATCH /:id/program/items/:zone_id`
//   (programs.service.patchProgramItem — validates composer editability +
//   resource approval, then upserts the one item). `composerId` flows from
//   layout-editor → PropertiesPanel → here; only when it is ABSENT
//   (test/preview context) does `handleSave` stay local-only (early return).
//   Covered by apps/server/tests/composers-program-items.test.ts +
//   apps/admin/tests/zone-binding-picker.test.tsx.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { listResources, getResourceSignedUrl } from '@/api/resources';
import { listComposers, patchProgramItem } from '@/api/composers';
import { MediaPreview } from '@/components/media/MediaPreview';
import { invalidateSignedUrl } from '@/components/media/useSignedMediaUrl';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { toast } from '@/lib/toast';
import type { ResourceSummary, ComposerSummary } from '@displaycms/contracts';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type BindingKind = 'image' | 'video' | 'web_page' | 'rss' | 'composer' | 'empty';

/**
 * Local-only UI shape for a zone binding.
 * NOTE: This does NOT map to a persisted contract field today.
 * W7 will define the ProgramItem contract union and server endpoint.
 */
export type ZoneBindingValue =
  | { kind: 'image'; resource_version_id: string }
  | { kind: 'video'; resource_version_id: string }
  | { kind: 'web_page'; url: string }
  | { kind: 'rss'; url: string }
  | { kind: 'composer'; composer_id: string }
  | { kind: 'empty' };

export interface ZoneBindingPickerProps {
  zoneId: string;
  currentBinding: ZoneBindingValue | null;
  /**
   * Called when the user confirms a new binding. Updates local UI state
   * immediately. If composerId is provided, also persists to the server.
   */
  onChange: (binding: ZoneBindingValue) => void | Promise<void>;
  /** W8 T2 — composer whose draft version is being edited. Required for persistence. */
  composerId?: string | undefined;
  /** W8 T2 — called after a binding is successfully saved so the parent can refetch. */
  onBindingSaved?: (() => void | Promise<void>) | undefined;
}

// ---------------------------------------------------------------------------
// ResourcePicker — cmdk autocomplete for image/video
// ---------------------------------------------------------------------------

interface ResourcePickerProps {
  kind: 'image' | 'video';
  onSelect: (resourceVersionId: string) => void;
}

function ResourcePicker({ kind, onSelect }: ResourcePickerProps): JSX.Element {
  const { t } = useTranslation('composers');
  const [resources, setResources] = useState<ResourceSummary[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    void listResources({ kind, per_page: 50, state: 'approved' }, { signal: controller.signal })
      .then((res) => {
        setResources(res.items);
        setLoading(false);

        // For images: pre-fetch signed URLs for the first 20 items to show thumbnails.
        if (kind === 'image') {
          res.items.slice(0, 20).forEach((r) => {
            if (!r.current_version_id) return;
            void getResourceSignedUrl(r.current_version_id, { signal: controller.signal })
              .then((signed) => {
                setSignedUrls((prev) => ({
                  ...prev,
                  [r.current_version_id as string]: signed.url,
                }));
              })
              .catch(() => {
                // signed URL failure is non-fatal; thumbnail simply won't show
              });
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, [kind]);

  const placeholder =
    kind === 'image'
      ? t('binding_source_placeholder_image')
      : t('binding_source_placeholder_video');

  return (
    <div className="mt-2" data-testid={`resource-picker-${kind}`}>
      {loading ? (
        <p className="text-xs text-muted-foreground">{t('picker.loading')}</p>
      ) : (
        <Command
          className="rounded-md border text-sm"
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <Command.Input placeholder={placeholder} className="px-2 py-1 text-sm" />
          <Command.List className="max-h-40 overflow-y-auto">
            <Command.Empty className="px-2 py-1 text-xs text-muted-foreground">
              {t('picker.empty')}
            </Command.Empty>
            {resources.map((r) => {
              const label =
                (r.name_i18n.fr ?? r.name_i18n.en ?? r.id);
              const rvId = r.current_version_id;
              if (!rvId) return null;
              const thumbUrl = kind === 'image' ? signedUrls[rvId] : undefined;
              return (
                <Command.Item
                  key={r.id}
                  value={label}
                  onSelect={() => onSelect(rvId)}
                  data-testid={`resource-item-${r.id}`}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-muted"
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt=""
                      className="h-8 w-8 rounded object-cover"
                      aria-hidden="true"
                      onError={() => {
                        // Bytes failed to load (e.g. expired signed URL): drop
                        // the dead URL so this row falls back to the neutral
                        // placeholder instead of a broken-image icon, and evict
                        // the cache entry so a re-open re-signs.
                        invalidateSignedUrl(rvId);
                        setSignedUrls((prev) => {
                          const next = { ...prev };
                          delete next[rvId];
                          return next;
                        });
                      }}
                    />
                  ) : kind === 'video' ? (
                    <MediaPreview
                      source="resource"
                      resourceVersionId={rvId}
                      kind="video"
                      size="thumb"
                    />
                  ) : (
                    <span className="h-8 w-8 rounded bg-muted" aria-hidden="true" />
                  )}
                  {label}
                </Command.Item>
              );
            })}
          </Command.List>
        </Command>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComposerPicker — cmdk autocomplete for nested composers
// ---------------------------------------------------------------------------

interface ComposerPickerProps {
  onSelect: (composerId: string) => void;
}

function ComposerPicker({ onSelect }: ComposerPickerProps): JSX.Element {
  const { t } = useTranslation('composers');
  const [composers, setComposers] = useState<ComposerSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    void listComposers({ per_page: 50 }, { signal: controller.signal })
      .then((res) => {
        setComposers(res.items);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return (
    <div className="mt-2" data-testid="composer-picker">
      {loading ? (
        <p className="text-xs text-muted-foreground">{t('picker.loading')}</p>
      ) : (
        <Command
          className="rounded-md border text-sm"
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <Command.Input
            placeholder={t('binding_source_placeholder_composer')}
            className="px-2 py-1 text-sm"
          />
          <Command.List className="max-h-40 overflow-y-auto">
            <Command.Empty className="px-2 py-1 text-xs text-muted-foreground">
              {t('picker.empty')}
            </Command.Empty>
            {composers.map((c) => {
              const label = c.name_i18n.fr ?? c.name_i18n.en ?? c.id;
              return (
                <Command.Item
                  key={c.id}
                  value={label}
                  onSelect={() => onSelect(c.id)}
                  data-testid={`composer-item-${c.id}`}
                  className="cursor-pointer px-2 py-1 hover:bg-muted"
                >
                  {label}
                </Command.Item>
              );
            })}
          </Command.List>
        </Command>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ZoneBindingPicker (public)
// ---------------------------------------------------------------------------

export function ZoneBindingPicker({
  zoneId,
  currentBinding,
  onChange,
  composerId,
  onBindingSaved,
}: ZoneBindingPickerProps): JSX.Element {
  const { t } = useTranslation('composers');

  const [kind, setKind] = useState<BindingKind>(currentBinding?.kind ?? 'empty');
  const [draftUrl, setDraftUrl] = useState<string>(
    currentBinding &&
      (currentBinding.kind === 'web_page' || currentBinding.kind === 'rss')
      ? currentBinding.url
      : '',
  );

  // Sync local kind when the parent pushes a new currentBinding (e.g. undo).
  useEffect(() => {
    setKind(currentBinding?.kind ?? 'empty');
    if (
      currentBinding &&
      (currentBinding.kind === 'web_page' || currentBinding.kind === 'rss')
    ) {
      setDraftUrl(currentBinding.url);
    } else {
      setDraftUrl('');
    }
  }, [currentBinding]);

  // W8 T2 — persist the binding via PATCH if composerId is available.
  async function handleSave(binding: ZoneBindingValue): Promise<void> {
    onChange(binding);
    if (!composerId) {
      // No composerId — binding is local-only (test/preview context).
      return;
    }
    // Map ZoneBindingValue to ProgramItemBinding (add zone_id from props).
    const apiBinding =
      binding.kind === 'image' ? { kind: 'image' as const, zone_id: zoneId, resource_version_id: binding.resource_version_id }
      : binding.kind === 'video' ? { kind: 'video' as const, zone_id: zoneId, resource_version_id: binding.resource_version_id }
      : binding.kind === 'web_page' ? { kind: 'web_page' as const, zone_id: zoneId, source_url: binding.url }
      : binding.kind === 'rss' ? { kind: 'rss' as const, zone_id: zoneId, source_url: binding.url }
      : binding.kind === 'composer' ? { kind: 'composer' as const, zone_id: zoneId, composer_id: binding.composer_id }
      : { kind: 'empty' as const, zone_id: zoneId };
    try {
      await patchProgramItem(composerId, zoneId, apiBinding);
      toast.success(t('binding_save_success'));
      if (onBindingSaved) await onBindingSaved();
    } catch {
      toast.error(t('binding_save_failed'));
    }
  }

  function handleApplyEmpty(): void {
    handleSave({ kind: 'empty' });
  }

  function handleApplyUrl(): void {
    if (!draftUrl) return;
    if (kind === 'web_page') {
      handleSave({ kind: 'web_page', url: draftUrl });
    } else if (kind === 'rss') {
      handleSave({ kind: 'rss', url: draftUrl });
    }
  }

  return (
    <div className="border-t pt-3" data-testid="zone-binding-picker">
      <h4 className="mb-2 text-xs font-semibold text-muted-foreground">
        {t('binding_section_title')}
      </h4>

      <label className="block text-xs">
        <span className="text-muted-foreground">{t('binding_type_label')}</span>
        <Select
          value={kind}
          onValueChange={(v) => setKind(v as BindingKind)}
        >
          <SelectTrigger className="mt-1 w-full" data-testid="binding-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="empty">{t('binding_type_empty')}</SelectItem>
            <SelectItem value="image">{t('binding_type_image')}</SelectItem>
            <SelectItem value="video">{t('binding_type_video')}</SelectItem>
            <SelectItem value="web_page">{t('binding_type_web_page')}</SelectItem>
            <SelectItem value="rss">{t('binding_type_rss')}</SelectItem>
            <SelectItem value="composer">{t('binding_type_composer')}</SelectItem>
          </SelectContent>
        </Select>
      </label>

      {kind === 'empty' && (
        <button
          type="button"
          onClick={handleApplyEmpty}
          className="mt-2 rounded bg-muted px-2 py-1 text-sm"
          data-testid="binding-clear"
        >
          {t('binding_clear')}
        </button>
      )}

      {(kind === 'image' || kind === 'video') && (
        <ResourcePicker
          kind={kind}
          onSelect={(rvId) => handleSave({ kind, resource_version_id: rvId })}
        />
      )}

      {(kind === 'web_page' || kind === 'rss') && (
        <div className="mt-2 space-y-2">
          <input
            type="url"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            placeholder={t(
              kind === 'web_page'
                ? 'binding_source_placeholder_web_page'
                : 'binding_source_placeholder_rss',
            )}
            className="w-full rounded border px-2 py-1 text-sm"
            data-testid={`binding-url-${kind}`}
          />
          <button
            type="button"
            onClick={handleApplyUrl}
            disabled={!draftUrl}
            className="rounded bg-primary px-2 py-1 text-sm text-primary-foreground disabled:opacity-40"
            data-testid="binding-save-url"
          >
            {t('binding_save')}
          </button>
        </div>
      )}

      {kind === 'composer' && (
        <ComposerPicker
          onSelect={(cId) => handleSave({ kind: 'composer', composer_id: cId })}
        />
      )}
    </div>
  );
}
